import React, { useState } from "react";
import { Users, Building2, Compass, Scale, Wallet, Send } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { icon: Building2, label: "Real Estate Agency" },
  { icon: Users, label: "Independent Property Consultant" },
  { icon: Building2, label: "Landowner" },
  { icon: Building2, label: "Developer" },
  { icon: Compass, label: "Survey Professional" },
  { icon: Building2, label: "Architect / Planner" },
  { icon: Scale, label: "Legal Professional" },
  { icon: Wallet, label: "Financial / Home Loan" },
];

export default function Partner() {
  const [form, setForm] = useState({ name: "", organization: "", contact: "", email: "", area_of_operation: "", partnership_category: CATEGORIES[0].label, message: "" });
  const [sub, setSub] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact || !form.area_of_operation) {
      toast.error("Please provide name, contact and area of operation.");
      return;
    }
    setSub(true);
    try {
      await api.post("/partnerships", form);
      toast.success("Partnership enquiry received. We will reach out to discuss next steps.");
      setForm({ name: "", organization: "", contact: "", email: "", area_of_operation: "", partnership_category: CATEGORIES[0].label, message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSub(false);
    }
  };

  const input = "w-full bg-white border border-[#E2E8F0] rounded-lg px-3.5 py-2.5 text-sm text-[#1F2937] focus:outline-none focus:border-[#183B66]";

  return (
    <>
      <section className="bg-[#101827] text-white">
        <div className="ae-container pt-28 pb-16">
          <div className="ae-eyebrow mb-3">Partner With Us</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold max-w-3xl">Building a Connected Real Estate Network.</h1>
          <p className="text-white/80 mt-5 max-w-3xl leading-relaxed">
            We work alongside agents, consultants, landowners, developers, surveyors, architects, legal
            professionals and finance partners. If you have an aligned mandate, we'd love to talk.
          </p>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <SectionHeader eyebrow="Collaboration Categories" title="Who we partner with" />
            <div className="grid sm:grid-cols-2 gap-3">
              {CATEGORIES.map((c, i) => (
                <div key={i} className="ae-card p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F4F6F8] flex items-center justify-center text-[#183B66]"><c.icon className="w-5 h-5" /></div>
                  <div className="font-semibold text-[#101827] text-sm">{c.label}</div>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <form onSubmit={submit} className="ae-card p-6 sticky top-28 space-y-3" data-testid="partnership-form">
              <div className="ae-eyebrow mb-1">Partnership Enquiry</div>
              <h3 className="font-display text-2xl font-semibold text-[#101827] mb-2">Tell us about yourself</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={input} placeholder="Full Name *" value={form.name} onChange={update("name")} data-testid="partner-name" />
                <input className={input} placeholder="Organisation" value={form.organization} onChange={update("organization")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={input} placeholder="Phone *" value={form.contact} onChange={update("contact")} data-testid="partner-contact" />
                <input className={input} placeholder="Email" value={form.email} onChange={update("email")} />
              </div>
              <input className={input} placeholder="Area of Operation *" value={form.area_of_operation} onChange={update("area_of_operation")} data-testid="partner-area" />
              <select className={input} value={form.partnership_category} onChange={update("partnership_category")}>
                {CATEGORIES.map((c) => <option key={c.label}>{c.label}</option>)}
              </select>
              <textarea className={input + " min-h-[90px]"} placeholder="Tell us more about your proposal" value={form.message} onChange={update("message")} />
              <button disabled={sub} className="ae-btn-primary w-full justify-center" data-testid="partner-submit">
                <Send className="w-4 h-4" /> {sub ? "Submitting…" : "Submit Partnership Enquiry"}
              </button>
            </form>
          </aside>
        </div>
      </section>
    </>
  );
}
